import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Download, RotateCw, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { downloadCsv } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (d) => new Date(d).toLocaleString("en-IN");

const STATUS_CLS = {
  paid: "bg-ptf-accent text-ptf-primary",
  pledge: "bg-ptf-secondary/15 text-ptf-secondary",
  interest: "bg-ptf-primary/10 text-ptf-primary",
  created: "bg-ptf-primary/10 text-ptf-primary",
};

function Donations() {
  const qc = useQueryClient();
  const [summaryYear, setSummaryYear] = useState(String(new Date().getFullYear()));
  const { data: donations = [] } = useQuery({ queryKey: ["donations"], queryFn: async () => (await api.get("/admin/donations")).data });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/donations/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["donations"] }); toast.success("Donation record deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const resendReceipt = useMutation({
    mutationFn: (id) => api.post(`/admin/donations/${id}/resend-receipt`),
    onSuccess: (res) => toast.success(res.data?.message || "Receipt resent."),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const sendAnnualSummary = useMutation({
    mutationFn: (year) => api.post(`/admin/donations/send-annual-summary?year=${year}`),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(`Sent to ${d.emails_sent} of ${d.champions_found} Champion${d.champions_found === 1 ? "" : "s"} (${d.skipped_no_donations_that_year} had no ${summaryYear} donations).`);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const exportCsv = () => downloadCsv(
    [["name", "email", "phone", "amount_inr", "frequency", "status", "message", "created_at"],
      ...donations.map((d) => [d.name, d.email, d.phone, d.amount, d.frequency, d.status, d.message, d.created_at])],
    "donations.csv"
  );
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Donations ({donations.length})</h2>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            value={summaryYear}
            onChange={(e) => setSummaryYear(e.target.value)}
            data-testid="annual-summary-year-input"
            className="w-20 border border-ptf-primary/20 px-2 py-2 text-xs"
          />
          <button
            onClick={() => { if (window.confirm(`Send the ${summaryYear} Annual Giving Summary email to every Champion (3+ lifetime donations)?`)) sendAnnualSummary.mutate(summaryYear); }}
            disabled={sendAnnualSummary.isPending}
            data-testid="send-annual-summary-btn"
            className="inline-flex items-center gap-2 border border-ptf-primary/20 px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary hover:bg-ptf-primary/5 transition-colors disabled:opacity-50"
          >
            <Mail className="h-3.5 w-3.5" /> Send Annual Giving Summary
          </button>
          {donations.length > 0 && (
            <button onClick={exportCsv} data-testid="export-donations-csv" className="inline-flex items-center gap-2 border border-ptf-primary/20 px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary hover:bg-ptf-primary/5 transition-colors">
              <Download className="h-3.5 w-3.5" /> Download CSV
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {donations.length === 0 && <p className="text-sm text-ptf-text/50">No donations yet.</p>}
        {donations.map((d) => (
          <div key={d.id} data-testid="donation-row" className="border border-ptf-primary/10 bg-white p-5">
            <div className="flex flex-wrap justify-between gap-2">
              <p className="font-medium text-ptf-primary">{d.name} · ₹{Number(d.amount || 0).toLocaleString("en-IN")} <span className="text-xs text-ptf-text/50">({d.frequency})</span></p>
              <span className="flex items-center gap-3">
                <span className={`text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 ${STATUS_CLS[d.status] || STATUS_CLS.interest}`}>{d.status}</span>
                <span className="text-xs text-ptf-text/45">{fmt(d.created_at)}</span>
                {d.status === "paid" && (
                  <button
                    onClick={() => resendReceipt.mutate(d.id)}
                    disabled={resendReceipt.isPending}
                    data-testid="resend-donation-receipt"
                    className="inline-flex items-center gap-1.5 border border-ptf-primary/15 px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-ptf-primary hover:bg-ptf-accent/20 transition-colors disabled:opacity-50"
                  >
                    <RotateCw className="h-3 w-3" /> Resend Receipt
                  </button>
                )}
                <button onClick={() => { if (window.confirm("Delete this donation record?")) del.mutate(d.id); }} data-testid="delete-donation" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-sm text-ptf-secondary mt-1">{d.email}{d.phone ? ` · ${d.phone}` : ""}</p>
            {d.message && <p className="text-sm text-ptf-text/75 mt-2">{d.message}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Volunteers() {
  const qc = useQueryClient();
  const { data: volunteers = [] } = useQuery({ queryKey: ["volunteers"], queryFn: async () => (await api.get("/admin/volunteers")).data });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/volunteers/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["volunteers"] }); toast.success("Volunteer removed"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const exportCsv = () => downloadCsv(
    [["name", "email", "phone", "location", "areas", "availability", "message", "created_at"],
      ...volunteers.map((v) => [v.name, v.email, v.phone, v.location, (v.areas || []).join("; "), v.availability, v.message, v.created_at])],
    "volunteers.csv"
  );
  return (
    <div className="mt-14">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Volunteers ({volunteers.length})</h2>
        {volunteers.length > 0 && (
          <button onClick={exportCsv} data-testid="export-volunteers-csv" className="inline-flex items-center gap-2 border border-ptf-primary/20 px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary hover:bg-ptf-primary/5 transition-colors">
            <Download className="h-3.5 w-3.5" /> Download CSV
          </button>
        )}
      </div>
      <div className="space-y-2">
        {volunteers.length === 0 && <p className="text-sm text-ptf-text/50">No volunteer registrations yet.</p>}
        {volunteers.map((v) => (
          <div key={v.id} data-testid="volunteer-row" className="border border-ptf-primary/10 bg-white p-5">
            <div className="flex flex-wrap justify-between gap-2">
              <p className="font-medium text-ptf-primary">{v.name}{v.location ? ` · ${v.location}` : ""}</p>
              <span className="flex items-center gap-3">
                <span className="text-xs text-ptf-text/45">{fmt(v.created_at)}</span>
                <button onClick={() => { if (window.confirm("Remove this volunteer?")) del.mutate(v.id); }} data-testid="delete-volunteer" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-sm text-ptf-secondary mt-1">{v.email}{v.phone ? ` · ${v.phone}` : ""}</p>
            <p className="text-xs uppercase tracking-[0.12em] text-ptf-text/55 mt-1">{(v.areas || []).join(" · ")}{v.availability ? ` · ${v.availability}` : ""}</p>
            {v.message && <p className="text-sm text-ptf-text/75 mt-2">{v.message}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Sponsorships() {
  const qc = useQueryClient();
  const { data: sponsorships = [] } = useQuery({ queryKey: ["sponsorships"], queryFn: async () => (await api.get("/admin/sponsorships")).data });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/sponsorships/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sponsorships"] }); toast.success("Sponsorship record deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const exportCsv = () => downloadCsv(
    [["peak", "name", "email", "phone", "amount_inr", "status", "message", "created_at"],
      ...sponsorships.map((s) => [s.peak, s.name, s.email, s.phone, s.amount, s.status, s.message, s.created_at])],
    "climb-sponsorships.csv"
  );
  return (
    <div className="mt-14">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Climb Sponsorships ({sponsorships.length})</h2>
        {sponsorships.length > 0 && (
          <button onClick={exportCsv} data-testid="export-sponsorships-csv" className="inline-flex items-center gap-2 border border-ptf-primary/20 px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary hover:bg-ptf-primary/5 transition-colors">
            <Download className="h-3.5 w-3.5" /> Download CSV
          </button>
        )}
      </div>
      <div className="space-y-2">
        {sponsorships.length === 0 && <p className="text-sm text-ptf-text/50">No climb sponsorship enquiries yet.</p>}
        {sponsorships.map((s) => (
          <div key={s.id} data-testid="sponsorship-row" className="border border-ptf-primary/10 bg-white p-5">
            <div className="flex flex-wrap justify-between gap-2">
              <p className="font-medium text-ptf-primary">{s.peak} · {s.name} · ₹{Number(s.amount || 0).toLocaleString("en-IN")}</p>
              <span className="flex items-center gap-3">
                <span className={`text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 ${STATUS_CLS[s.status] || STATUS_CLS.interest}`}>{s.status}</span>
                <span className="text-xs text-ptf-text/45">{fmt(s.created_at)}</span>
                <button onClick={() => { if (window.confirm("Delete this sponsorship record?")) del.mutate(s.id); }} data-testid="delete-sponsorship" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-sm text-ptf-secondary mt-1">{s.email}{s.phone ? ` · ${s.phone}` : ""}</p>
            {s.message && <p className="text-sm text-ptf-text/75 mt-2">{s.message}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

const PAY_CLS = {
  PAID: "bg-ptf-accent text-ptf-primary",
  ORDER_CREATED: "bg-ptf-secondary/15 text-ptf-secondary",
  INTENT: "bg-ptf-primary/10 text-ptf-primary",
  FAILED: "bg-red-100 text-red-700",
};

const PAY_KINDS = ["ALL", "DONATION", "SPONSORSHIP", "EXPEDITION", "TRAINING"];
const PAY_STATUSES = ["ALL", "PAID", "FAILED", "ORDER_CREATED", "INTENT"];

function Payments() {
  const qc = useQueryClient();
  const [kind, setKind] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const { data: payments = [] } = useQuery({ queryKey: ["admin-payments"], queryFn: async () => (await api.get("/admin/payments")).data });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/payments/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-payments"] }); toast.success("Payment record deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const filtered = payments.filter((p) => (kind === "ALL" || p.kind === kind) && (status === "ALL" || p.status === status));
  const exportCsv = () => downloadCsv(
    [["kind", "item", "name", "email", "amount", "currency", "status", "order_id", "payment_id", "created_at"],
      ...filtered.map((p) => [p.kind, p.item_title, p.name, p.email, p.amount, p.currency, p.status, p.order_id, p.payment_id, p.created_at])],
    "payments.csv"
  );
  return (
    <div className="mb-14">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Payments ({filtered.length} of {payments.length})</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="payments-filter-kind" className="w-[150px] h-9 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              {PAY_KINDS.map((k) => <SelectItem key={k} value={k}>{k === "ALL" ? "All types" : k}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="payments-filter-status" className="w-[150px] h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {PAY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "ALL" ? "All statuses" : s}</SelectItem>)}
            </SelectContent>
          </Select>
          {filtered.length > 0 && (
            <button onClick={exportCsv} data-testid="export-payments-csv" className="inline-flex items-center gap-2 border border-ptf-primary/25 px-4 py-2 text-[0.65rem] uppercase tracking-[0.14em] text-ptf-primary hover:bg-ptf-primary/5">
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-sm text-ptf-text/50">No payment records match this filter.</p>}
        {filtered.map((p) => (
          <div key={p.id} data-testid="payment-row" className="border border-ptf-primary/10 bg-white px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px]">
              <p className="font-medium text-ptf-primary text-sm">{p.name || "-"} · {p.item_title || p.kind}</p>
              <p className="text-[0.62rem] text-ptf-text/55 mt-0.5">{p.email}{p.order_id ? ` · Order ${p.order_id}` : ""}{p.payment_id ? ` · Payment ${p.payment_id}` : ""}</p>
              {p.programme_name && <p className="text-[0.6rem] uppercase tracking-[0.1em] text-ptf-secondary mt-1">Programme: {p.programme_name}</p>}
            </div>
            <span className="text-sm font-semibold text-ptf-primary">₹{Number(p.amount || 0).toLocaleString("en-IN")}</span>
            <span className={`text-[0.58rem] uppercase tracking-[0.12em] px-2.5 py-1 ${PAY_CLS[p.status] || "bg-ptf-primary/10 text-ptf-primary"}`}>
              {p.status === "INTENT" ? "Intent · Not collected" : p.status}
            </span>
            <span className="text-xs text-ptf-text/45">{fmt(p.created_at)}</span>
            <button onClick={() => del.mutate(p.id)} data-testid={`delete-payment-${p.id}`} className="text-ptf-text/40 hover:text-red-600 transition-colors" aria-label="Delete payment record">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SupportersAdmin() {
  return (
    <div>
      <Payments />
      <Donations />
      <Sponsorships />
      <Volunteers />
    </div>
  );
}
